import React, { useState, useRef, useEffect } from 'react';
import UserProfile from './UserProfile';
import JourneyGoal from './JourneyGoal';
import { UserJourney } from '@/types/journey';
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { PencilLine, Check, CheckCircle2 } from "lucide-react";
import { useIsMobile } from '@/hooks/use-mobile';

interface JourneyProfileSectionProps {
  journey: UserJourney;
  onUpdate: (updatedProfile: Partial<UserJourney>) => void;
  completionPercentage: number;
}

const JourneyProfileSection: React.FC<JourneyProfileSectionProps> = ({
  journey,
  onUpdate,
  completionPercentage
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(journey.mapName);
  const inputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    setEditValue(journey.mapName);
  }, [journey.mapName]);

  const handleEditComplete = () => {
    if (editValue.trim() !== '') {
      onUpdate({ mapName: editValue });
    } else {
      setEditValue(journey.mapName);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleEditComplete();
    } else if (e.key === 'Escape') {
      setEditValue(journey.mapName);
      setIsEditing(false);
    }
  };

  return (
    <div className={`w-full bg-card rounded-lg shadow-sm ${isMobile ? 'p-2' : 'p-4'} border border-border`}>
      {/* Map name and progress section */}
      <div className="mb-3">
        {/* Map name input */}
        <div className="flex items-center w-full gap-2 mb-2">
          {isEditing ? (
            <div className="flex-1 flex items-center gap-2">
              <Input
                ref={inputRef}
                name="mapName"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={handleEditComplete}
                onKeyDown={handleKeyDown}
                placeholder="Enter journey map name"
                className={`h-10 ${isMobile ? 'text-lg' : 'text-xl'} font-medium`}
              />
            </div>
          ) : (
            <div 
              className="flex-1 flex items-center gap-2 cursor-pointer hover:bg-secondary/50 dark:hover:bg-secondary/20 px-2 py-1 rounded"
              onClick={() => setIsEditing(true)}
            >
              <h2 className={`${isMobile ? 'text-lg' : 'text-xl'} font-medium`}>
                {journey.mapName || "Untitled Journey Map"}
              </h2>
              <PencilLine className="h-3.5 w-3.5 text-muted-foreground" />
            </div>
          )}
        </div>
        
        {/* Completion percentage indicator */}
        <div className="flex items-center w-full gap-2 pl-2">
          <div className="text-xs text-muted-foreground min-w-14 flex items-center">
            {completionPercentage}% complete
          </div>
          <Progress value={completionPercentage} className="h-2 max-w-xs" />
          <CheckCircle2 
            className={`h-5 w-5 ml-1 ${completionPercentage === 100 ? 'text-emerald-500' : 'text-gray-300 dark:text-gray-600'}`} 
            aria-label={completionPercentage === 100 ? "Journey complete" : "Journey incomplete"}
          />
        </div>
      </div>

      <div className={`grid ${isMobile ? 'grid-cols-1 gap-2' : 'grid-cols-1 md:grid-cols-2 gap-4'}`}>
        <div className={isMobile ? 'p-2' : ''}>
          <UserProfile 
            userName={journey.userName}
            userRole={journey.userRole}
            userPhoto={journey.userPhoto}
            onUpdate={onUpdate}
            isComplete={!!(journey.userName && journey.userRole)}
          />
        </div>
        
        <div className={isMobile ? 'p-2' : ''}>
          <JourneyGoal
            scenario={journey.scenario}
            goal={journey.goal}
            expectations={journey.expectations || []}
            onUpdate={onUpdate}
            isComplete={!!(journey.scenario && journey.goal && (journey.expectations && journey.expectations.length > 0))}
          />
        </div>
      </div>
    </div>
  );
};

export default JourneyProfileSection;
